import React, {useMemo} from 'react';

import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import useStepTypeInfos, {StepTypeInfo} from 'pipelines/hooks/useStepTypeInfosQuery';
import {getUserFacingStepTypeName} from 'pipelines/StepTypeInfoUtils';
import BetaBadge from 'ui-library/badges/BetaBadge';
import MenuPanel, {MenuPanelSection} from 'ui-library/menus/MenuPanel';
import strUtil from 'util/strUtil';

interface StepTypeMenuProps {
  /**
   * Filter which step types are included in the menu.
   *
   * Callers are responsible for hiding step types the user cannot create or run; compose
   * `useCheckStepTypeEnabledForUser` into this filter.
   */
  filterStepTypes?: (stepType: PipelineStepType) => boolean;
  selectedStepType?: PipelineStepType | null;
  onSelectStepType?: (stepType: PipelineStepType | null) => void;
  showDescription?: boolean;
  /**
   * Search query to filter step types by label (case-insensitive).
   */
  searchQuery?: string;
}

const TAG_TO_BADGE_COMPONENT: Partial<Record<string, JSX.Element>> = {
  beta: <BetaBadge />,
};

/**
 * Function for organizing step types into groups.
 *
 * @param stepTypes list of step types to sort by group
 * @returns a record of groups and their corresponding step types
 */
function sortStepTypesByGroup(
  stepTypes: Array<PipelineStepType>,
  stepTypeInfos: Map<PipelineStepType, StepTypeInfo>
): Record<string, Array<PipelineStepType>> {
  const stepTypesByGroup: Record<string, Array<PipelineStepType>> = {};
  for (const stepType of stepTypes) {
    const groups = stepTypeInfos.get(stepType)?.groups ?? [];
    const group = groups[0] ?? 'Other';
    if (stepTypesByGroup[group] === undefined) {
      stepTypesByGroup[group] = [];
    }
    stepTypesByGroup[group].push(stepType);
  }
  const sortedGroups = Object.keys(stepTypesByGroup)
    .filter((group) => group !== 'Other')
    .sort((a, b) => a.localeCompare(b));
  if (stepTypesByGroup.Other) {
    sortedGroups.push('Other');
  }
  const sortedStepTypesByGroup: Record<string, Array<PipelineStepType>> = {};
  for (const group of sortedGroups) {
    sortedStepTypesByGroup[group] = stepTypesByGroup[group].toSorted((a, b) =>
      getUserFacingStepTypeName(a, stepTypeInfos).localeCompare(getUserFacingStepTypeName(b, stepTypeInfos))
    );
  }
  return sortedStepTypesByGroup;
}

/**
 * Menu for selecting pipeline step types.
 */
export default function StepTypeMenu({
  filterStepTypes,
  selectedStepType,
  onSelectStepType,
  showDescription,
  searchQuery,
}: StepTypeMenuProps): JSX.Element {
  const stepTypeInfos = useStepTypeInfos();

  // Get all available step types (excluding search filtering)
  const availableStepTypes = useMemo(
    () => Object.values(PipelineStepType).filter((stepType) => filterStepTypes?.(stepType) ?? true),
    [filterStepTypes]
  );

  // Apply search filtering for display purposes only
  const filteredStepTypes = useMemo(
    () =>
      availableStepTypes.filter((stepType) => {
        if (!searchQuery) {
          return true;
        }
        const label = getUserFacingStepTypeName(stepType, stepTypeInfos);
        const groups = stepTypeInfos.get(stepType)?.groups ?? [];
        // Match if label contains search query OR any of the step's groups match
        return (
          strUtil.caseInsensitiveContains(label, searchQuery) ||
          groups.some((group) => strUtil.caseInsensitiveContains(group, searchQuery))
        );
      }),
    [availableStepTypes, searchQuery, stepTypeInfos]
  );

  const stepTypesByGroup = sortStepTypesByGroup(filteredStepTypes, stepTypeInfos);

  const sections: Array<MenuPanelSection> = Object.entries(stepTypesByGroup)
    .filter(([, stepTypes]) => stepTypes.length > 0)
    .map(([group, stepTypes]) => ({
      header: group,
      rows: stepTypes.map((stepType) => {
        const tags = stepTypeInfos.get(stepType)?.tags ?? [];
        // TODO: Support more than one tag. There is currently no demand for more than one tag, but
        // a refactor of the MenuPanelRow component will be required.
        const badge = tags.length > 0 ? TAG_TO_BADGE_COMPONENT[tags[0]] : undefined;
        return {
          id: stepType,
          label: getUserFacingStepTypeName(stepType, stepTypeInfos),
          description: stepTypeInfos.get(stepType)?.description ?? undefined,
          badge,
        };
      }),
    }));

  if (sections.length === 0) {
    return (
      <MenuPanel
        sections={[
          {
            rows: [
              {
                id: 'no-step-types',
                label: 'No step types are available',
                isDisabled: true,
              },
            ],
          },
        ]}
      />
    );
  }

  return (
    <MenuPanel
      dataTestAttrs={{component: 'StepTypeMenu', element: 'root'}}
      sections={sections}
      selectedId={selectedStepType ?? null}
      onSelectId={(id) => {
        onSelectStepType?.(id as PipelineStepType);
      }}
      showDescriptions={showDescription}
    />
  );
}
